import { useCallback, useEffect, useState } from "react";

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

// reCAPTCHA v3 site key (public, safe to expose in client code)
const RECAPTCHA_SITE_KEY = "6LdBglQsAAAAAH4jchkoXurZin8nFxt9ikpQpIpU";

export function useRecaptcha() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if already loaded
    if (window.grecaptcha) {
      setIsLoaded(true);
      return;
    }

    // Load reCAPTCHA script
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      window.grecaptcha.ready(() => {
        setIsLoaded(true);
      });
    };

    script.onerror = () => {
      setError("Failed to load reCAPTCHA");
      setIsLoaded(true); // Allow form to work if reCAPTCHA fails
    };

    document.head.appendChild(script);

    // Don't clean up - reCAPTCHA should persist once loaded
  }, []);

  const executeRecaptcha = useCallback(
    async (action: string): Promise<string | null> => {
      if (!window.grecaptcha) {
        console.warn("reCAPTCHA not loaded");
        return null;
      }

      try {
        const token = await window.grecaptcha.execute(RECAPTCHA_SITE_KEY, { action });
        return token;
      } catch (err) {
        console.error("reCAPTCHA execution failed:", err);
        return null;
      }
    },
    []
  );

  return {
    isLoaded,
    error,
    executeRecaptcha,
  };
}
